/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * The organizer behind an event — the club or company running it.
 *
 * A single table, so this is simpler than the event: one operation, no `@ref`
 * linking. It still goes through the batch endpoint rather than the
 * single-record one, because that is the path an organization admin is
 * authorised on.
 */

/** Exactly the values the verification_status column allows. */
export const VERIFICATION_STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'BLOCKED'];

/** Every column the organization table declares that a person fills in. */
const ORGANIZATION_COLUMNS = [
  'name', 'about_information', 'organization_type_id', 'address_line', 'city', 'state', 'country',
  'pin_code', 'government_id', 'government_id_proof', 'official_website_url', 'contact_phone',
  'support_email', 'contact_email', 'logo_url', 'verification_status',
  'blocked_reason', 'rejection_reason',
];

const REQUIRED_FIELDS = {
  name: 'Name the organization.',
  organization_type_id: 'Choose what kind of organization this is.',
  address_line: 'Enter the street address.',
  city: 'Enter the city.',
  state: 'Enter the state.',
  country: 'Enter the country.',
  pin_code: 'Enter the PIN code.',
  support_email: 'Enter the support email address.',
  contact_email: 'Enter the contact email address.',
};

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Deliberately permissive: the column is text, and the platform is the
// authority. This only catches an obvious typo before a round trip.
const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

export function initialOrganizationForm() {
  return {
    name: '',
    about_information: '',
    organization_type_id: '',
    address_line: '',
    city: '',
    state: '',
    country: 'India',
    pin_code: '',
    government_id: '',
    government_id_proof: '',
    official_website_url: '',
    contact_phone: '',
    support_email: '',
    contact_email: '',
    logo_url: '',
    // Verifying an organizer is a decision someone makes, never a default.
    verification_status: 'PENDING',
    blocked_reason: '',
    rejection_reason: '',
  };
}

export function validateOrganizationForm(form) {
  const errors = {};
  const value = form || {};

  Object.entries(REQUIRED_FIELDS).forEach(([field, message]) => {
    if (isBlank(value[field])) errors[field] = message;
  });

  ['support_email', 'contact_email'].forEach((field) => {
    if (!isBlank(value[field]) && !looksLikeEmail(value[field])) {
      errors[field] = 'Enter a valid email address.';
    }
  });

  // A refusal that records no reason is unusable to whoever reads it later.
  if (value.verification_status === 'REJECTED' && isBlank(value.rejection_reason)) {
    errors.rejection_reason = 'Say why this organization was rejected.';
  }
  if (value.verification_status === 'BLOCKED' && isBlank(value.blocked_reason)) {
    errors.blocked_reason = 'Say why this organization is blocked.';
  }

  return errors;
}

export function buildOrganizationBatch(form, { organizationId, applicationId }) {
  const data = {};
  ORGANIZATION_COLUMNS.forEach((column) => {
    const value = form?.[column];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      data[column] = value;
    }
  });

  const exists = form?.id !== undefined && form?.id !== null && form?.id !== '';
  if (exists) data.id = form.id;

  return {
    organization_id: organizationId,
    application_id: applicationId,
    operations: [{ table: 'organization', data }],
  };
}

export function hydrateOrganizationForm(row) {
  if (!row) return null;
  const form = { ...initialOrganizationForm(), id: row.id };
  ORGANIZATION_COLUMNS.forEach((column) => {
    // A null column becomes '' so the input stays controlled.
    form[column] = row[column] === null || row[column] === undefined ? '' : row[column];
  });
  if (!form.verification_status) form.verification_status = 'PENDING';
  return form;
}
